import type { BuildPhase } from "@hatch/shared";
import { PHASE_ORDER } from "@hatch/shared";

/**
 * The architecture, as the build fills it in.
 *
 * Four nodes over a grid, wired together — the composition the design puts at
 * the centre of its spec document. What each node can honestly SAY changes
 * partway through the build, and that change is the whole design of this
 * component.
 *
 * The tempting version of this shows a live file count per node. It does not
 * work, and the reason is worth writing down so nobody rediscovers it: the
 * entire Xano backend is DSL calls inside a single `xano/index.ts`, so a file
 * count of it is 1 no matter what was built. `api/src/agent/backend-counts.ts`
 * says so in its header, and it is why that module exists at all. Three nodes
 * reading "1 file" is not a diagram of anything.
 *
 * So a node shows its STATE until a real number exists for it, and the real
 * number replaces the state when one arrives:
 *
 *   waiting  →  building  →  built  →  12 tables
 *
 * State is a fact the phase already carries. The counts arrive on the terminal
 * snapshot, from source Hatch actually deployed. Nothing in between is invented,
 * and in particular nothing here ever renders a `0` it was not given — the
 * absent-means-unknown rule is `SessionSnapshot`'s, and a "0 tables" under a
 * working backend is the exact dishonesty it exists to prevent.
 *
 * The live, true, build-wide numbers live in the footer instead, where they
 * describe the build rather than pretending to describe a node.
 */

export interface SpecObjects {
  tables?: number;
  apis?: number;
  functions?: number;
}

type NodeState = "waiting" | "building" | "built" | "stopped";

/**
 * The three nodes, in the order the request travels: data, the API over it, and
 * the interface that calls it.
 *
 * There was a fourth, Functions, and it was dropped rather than kept honest.
 * It read `0` on every one of the first 33 successful builds — a real zero, not
 * an unknown: `api/src/agent/backend-counts.ts` only assigns zero to a kind when
 * the deployed source plainly registers others, and the agent simply never
 * calls `.registerFunctions([…])`. A tile that shows the same number on every
 * build is not reporting on this prompt, and "0 functions" turning green beside
 * "5 tables" announces the completion of something that was never there.
 *
 * Postgres and API both belong to `backend` and still settle together, which is
 * truthful: the whole backend is written in one pass into a single
 * `xano/index.ts`, so there is no moment where the tables are done and the
 * endpoints are not. Staggering them would be animation pretending to be
 * observation.
 */
const NODES = [
  { id: "postgres", label: "Postgres", phase: "backend", unit: "table" },
  { id: "api", label: "API", phase: "backend", unit: "endpoint" },
  { id: "app", label: "Front end", phase: "frontend", unit: "page" },
] as const satisfies ReadonlyArray<{
  id: string;
  label: string;
  phase: BuildPhase;
  unit: string;
}>;

const STATE_WORD: Record<NodeState, string> = {
  waiting: "waiting",
  building: "building",
  built: "built",
  stopped: "stopped",
};

function nodeState(owning: BuildPhase, phase: BuildPhase, failed: boolean): NodeState {
  const at = PHASE_ORDER[owning];
  const now = PHASE_ORDER[phase] ?? 0;
  if (now < at) return "waiting";
  if (now > at) return "built";
  return failed ? "stopped" : "building";
}

/**
 * The count for a node, or `undefined` when there is none to show.
 *
 * `undefined` and `0` are different answers and are kept different all the way
 * to the render. A backend with no functions reports `functions: 0` and should
 * say so; a build whose source could not be read reports nothing at all, and a
 * zero invented from that absence would be the screen lying about the one thing
 * it exists to prove.
 */
function nodeCount(
  id: (typeof NODES)[number]["id"],
  objects: SpecObjects | null,
  sitePages: number | null,
): number | undefined {
  switch (id) {
    case "postgres":
      return objects?.tables;
    case "api":
      return objects?.apis;
    case "app":
      return sitePages ?? undefined;
  }
}

export function SpecPanel({
  phase,
  failed,
  objects,
  sitePages,
  fileCount,
  caption,
}: {
  phase: BuildPhase;
  failed: boolean;
  objects: SpecObjects | null;
  sitePages: number | null;
  /** Build-wide and live. Real, unlike a per-node file count. */
  fileCount: number;
  /** The line under the diagram. The reveal changes it; the build does not. */
  caption?: string;
}) {
  return (
    <section
      aria-label="Architecture"
      className="deck-spec deck-grid rounded-xl border border-deck-line bg-deck-raise/50 p-3 sm:p-4"
      style={{ "--deck-grid-size": "22px" } as React.CSSProperties}
    >
      <div className="relative">
        {/*
          The spine: one hairline running behind all three tiles, which are
          opaque, so all that shows is the short run of line in each gap. Three
          boxes on a wire read as a pipeline — data, the API over it, the
          interface that calls it — which is the shape the design's own
          architecture panel is drawn in, and it costs one div rather than the
          per-pair connectors the same picture would otherwise need.
        */}
        <div aria-hidden className="deck-spec-spine pointer-events-none absolute inset-0">
          <div className="absolute inset-x-4 top-1/2 h-px -translate-y-1/2 bg-deck-line" />
        </div>

        <div className="deck-spec-grid relative grid grid-cols-3 gap-2 sm:gap-3">
          {NODES.map((node) => (
            <NodeTile
              key={node.id}
              label={node.label}
              unit={node.unit}
              state={nodeState(node.phase, phase, failed)}
              count={nodeCount(node.id, objects, sitePages)}
            />
          ))}
        </div>
      </div>

      <p className="deck-spec-caption mt-3 flex items-baseline justify-between gap-3 font-deck-mono text-[10px] tracking-[0.12em] text-deck-text-faint uppercase">
        <span>{caption ?? "Generated from your prompt"}</span>
        {/* Build-wide and live. The one count on this panel that is true at
            every moment of the build, which is why it is down here describing
            the build rather than up there describing a node.
            
            Absent below one, the same way the log header handles it. "0 files"
            is technically true at the start of every build and reads as a
            result rather than as a beginning. */}
        {fileCount > 0 && (
          <span className="shrink-0 tabular-nums">
            {fileCount} {fileCount === 1 ? "file" : "files"}
          </span>
        )}
      </p>
    </section>
  );
}

function NodeTile({
  label,
  unit,
  state,
  count,
}: {
  label: string;
  unit: string;
  state: NodeState;
  count: number | undefined;
}) {
  const live = state === "building";
  return (
    // Grouped and named so a screen reader announces "Postgres — 12 tables"
    // rather than four unlabelled numbers in a row. The visible label is a mono
    // micro-label and is the group's name, so the two cannot drift.
    <div
      role="group"
      aria-label={label}
      className={`deck-spec-tile rounded-lg border bg-deck-sunk px-3 py-2.5 transition-colors ${
        live
          ? "border-deck-blue shadow-[0_0_0_1px_var(--color-deck-blue-soft),0_0_24px_-6px_var(--color-deck-blue-glow)]"
          : "border-deck-line"
      }`}
    >
      <div className="flex min-w-0 items-center gap-1.5">
        <span
          aria-hidden
          className={`size-1.5 shrink-0 rounded-full ${DOT[state]} ${
            live ? "animate-[deckPulse_1.4s_ease-in-out_infinite]" : ""
          }`}
        />
        <span className="truncate font-deck-mono text-[10px] font-medium tracking-[0.14em] text-deck-text-faint uppercase">
          {label}
        </span>
      </div>

      {count === undefined ? (
        // No number yet. The state is a fact the phase already carries, so the
        // tile says that rather than sitting empty or inventing a zero.
        <p className="deck-spec-value mt-1.5 text-[13px] text-deck-text-faint">
          {STATE_WORD[state]}
        </p>
      ) : (
        <p className="deck-spec-value mt-1 flex animate-[deckFadeIn_0.35s_ease-out] flex-wrap items-baseline gap-x-1.5">
          <span className="deck-spec-count text-[22px] leading-none font-bold tabular-nums text-deck-text">
            {count}
          </span>
          <span className="font-deck-mono text-[11px] text-deck-text-soft">
            {count === 1 ? unit : `${unit}s`}
          </span>
        </p>
      )}
    </div>
  );
}

/**
 * `stopped` reads as ink rather than as the accent, for the reason the hatch
 * pills already record: the accent means here and now, and a stopped build is
 * neither. Sharing it would leave a halted node differing from a live one only
 * by a missing pulse — a distinction nobody catches on a still screen, and one
 * that vanishes entirely under reduced motion.
 */
const DOT: Record<NodeState, string> = {
  waiting: "bg-deck-line",
  building: "bg-deck-blue",
  built: "bg-deck-live",
  stopped: "bg-deck-text-faint",
};
